import React, { useRef, useEffect } from 'react';
import { Select, Popover } from 'antd';
import {
    SearchOutlined,
    PlusOutlined,
    FilterOutlined,
    ClearOutlined,
} from '@ant-design/icons';
import TaskViewTabs from '@/components/ProjectManagement/TaskViewTabs';

const STATUS_OPTIONS = [
    { label: 'Backlog / Upcoming', value: 'Backlog' },
    { label: 'In Progress', value: 'In Progress' },
    { label: 'Under Review', value: 'Under Review' },
    { label: 'On Hold', value: 'On Hold' },
    { label: 'Completed', value: 'Completed' },
];

const PRIORITY_OPTIONS = [
    { label: 'Low', value: 'low' },
    { label: 'Medium', value: 'medium' },
    { label: 'High', value: 'high' },
    { label: 'Urgent', value: 'urgent' },
];

const DUE_DATE_OPTIONS = [
    { label: 'Due Today', value: 'today' },
    { label: 'Due This Week', value: 'week' },
    { label: 'Overdue', value: 'overdue' },
];

export default function MyTasksControlsBar({
    activeTab = 'list',
    onTabChange,
    searchQuery = '',
    onSearchChange,
    searchResults = [],
    searchLoading = false,
    searchDropdownOpen = false,
    setSearchDropdownOpen,
    onTaskClick,
    selectedStatus = null,
    onStatusChange,
    selectedProject = null,
    onProjectChange,
    selectedPriority = null,
    onPriorityChange,
    selectedDueDate = null,
    onDueDateChange,
    selectedSprint = null,
    onSprintChange,
    selectedMilestone = null,
    onMilestoneChange,
    onResetFilters,
    availableProjects = [],
    availableSprints = [],
    availableMilestones = [],
    onNewTask,
}) {
    const searchContainerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
                setSearchDropdownOpen?.(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [setSearchDropdownOpen]);

    // Count active secondary filters (excluding status which has its own visible pill)
    const activeSecondaryCount = [
        selectedProject,
        selectedPriority,
        selectedDueDate,
        selectedSprint,
        selectedMilestone,
    ].filter(Boolean).length;

    const filterPopoverContent = (
        <div className="w-72 p-3 space-y-3">
            <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700 pb-2">
                <span className="text-xs font-bold text-slate-800 dark:text-slate-100">
                    Filter Assigned Tasks
                </span>
                {activeSecondaryCount > 0 && (
                    <button
                        type="button"
                        onClick={onResetFilters}
                        className="text-xs text-rose-500 hover:text-rose-700 flex items-center gap-1 cursor-pointer"
                    >
                        <ClearOutlined /> Reset
                    </button>
                )}
            </div>

            <div className="space-y-2.5 text-xs">
                {/* Project Filter */}
                <div>
                    <label className="block text-[11px] font-medium text-slate-500 dark:text-slate-400 mb-1">
                        Project
                    </label>
                    <Select
                        size="small"
                        allowClear
                        placeholder="All Projects"
                        value={selectedProject}
                        onChange={onProjectChange}
                        className="w-full text-xs"
                        options={availableProjects.map((p) => ({
                            label: p.title,
                            value: p.id,
                        }))}
                    />
                </div>

                {/* Status Filter */}
                <div>
                    <label className="block text-[11px] font-medium text-slate-500 dark:text-slate-400 mb-1">
                        Status
                    </label>
                    <Select
                        size="small"
                        allowClear
                        placeholder="All Statuses"
                        value={selectedStatus}
                        onChange={onStatusChange}
                        className="w-full text-xs"
                        options={STATUS_OPTIONS}
                    />
                </div>

                {/* Priority Filter */}
                <div>
                    <label className="block text-[11px] font-medium text-slate-500 dark:text-slate-400 mb-1">
                        Priority
                    </label>
                    <Select
                        size="small"
                        allowClear
                        placeholder="All Priorities"
                        value={selectedPriority}
                        onChange={onPriorityChange}
                        className="w-full text-xs"
                        options={PRIORITY_OPTIONS}
                    />
                </div>

                {/* Due Date Filter */}
                <div>
                    <label className="block text-[11px] font-medium text-slate-500 dark:text-slate-400 mb-1">
                        Due Date
                    </label>
                    <Select
                        size="small"
                        allowClear
                        placeholder="Any Time"
                        value={selectedDueDate}
                        onChange={onDueDateChange}
                        className="w-full text-xs"
                        options={DUE_DATE_OPTIONS}
                    />
                </div>

                {/* Sprint Filter */}
                {availableSprints.length > 0 && (
                    <div>
                        <label className="block text-[11px] font-medium text-slate-500 dark:text-slate-400 mb-1">
                            Sprint
                        </label>
                        <Select
                            size="small"
                            allowClear
                            placeholder="All Sprints"
                            value={selectedSprint}
                            onChange={onSprintChange}
                            className="w-full text-xs"
                            options={availableSprints.map((s) => ({
                                label: s,
                                value: s,
                            }))}
                        />
                    </div>
                )}

                {/* Milestone Filter */}
                {availableMilestones.length > 0 && (
                    <div>
                        <label className="block text-[11px] font-medium text-slate-500 dark:text-slate-400 mb-1">
                            Milestone
                        </label>
                        <Select
                            size="small"
                            allowClear
                            placeholder="All Milestones"
                            value={selectedMilestone}
                            onChange={onMilestoneChange}
                            className="w-full text-xs"
                            options={availableMilestones.map((m) => ({
                                label: m.title,
                                value: m.id,
                            }))}
                        />
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pb-1">
            {/* Left: Reused Standard View Tabs (List | Kanban | Timeline | Sprint) */}
            <div data-tour="task-view-tabs">
                <TaskViewTabs
                    activeTab={activeTab}
                    onTabChange={onTabChange}
                />
            </div>

            {/* Right: Status Pill, Filter Popover, Search Bar & + New Task */}
            <div className="flex flex-wrap items-center gap-2.5 w-full sm:w-auto justify-end">
                {/* Active Status Pill from Donut click or filter */}
                {selectedStatus && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 border border-blue-200 dark:border-blue-800 shadow-2xs">
                        <span>Status: {selectedStatus}</span>
                        <button
                            type="button"
                            onClick={() => onStatusChange?.(null)}
                            className="text-blue-400 hover:text-rose-500 font-bold ml-0.5 cursor-pointer leading-none"
                            title="Clear status filter"
                        >
                            ✕
                        </button>
                    </span>
                )}

                {/* Filter Popover Button */}
                <Popover
                    content={filterPopoverContent}
                    trigger="click"
                    placement="bottomRight"
                >
                    <button
                        type="button"
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all cursor-pointer ${
                            activeSecondaryCount > 0
                                ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700'
                                : 'bg-white dark:bg-[#1e293b] text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-50'
                        }`}
                        title="Filter assigned tasks"
                    >
                        <FilterOutlined style={{ fontSize: 12 }} />
                        <span>Filter</span>
                        {activeSecondaryCount > 0 && (
                            <span className="w-4 h-4 rounded-full bg-blue-600 text-white text-[10px] flex items-center justify-center font-bold">
                                {activeSecondaryCount}
                            </span>
                        )}
                    </button>
                </Popover>

                {/* Spotlight Quick-Find Search Bar & Live Dropdown */}
                <div ref={searchContainerRef} className="relative">
                    <SearchOutlined
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                        style={{ fontSize: 14 }}
                    />
                    <input
                        type="text"
                        value={searchQuery}
                        onFocus={() => {
                            if ((searchQuery || '').trim().length > 0) setSearchDropdownOpen?.(true);
                        }}
                        onChange={(e) => {
                            onSearchChange?.(e.target.value);
                            if (e.target.value.trim().length > 0) setSearchDropdownOpen?.(true);
                            else setSearchDropdownOpen?.(false);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') setSearchDropdownOpen?.(false);
                        }}
                        placeholder="Search tasks..."
                        className="bg-white dark:bg-[#1e293b] border border-slate-200 dark:border-slate-700 rounded-xl pl-8 pr-7 py-1.5 text-xs text-slate-800 dark:text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-blue-500 outline-none w-44 sm:w-60 transition-all shadow-2xs"
                    />
                    {searchQuery && (
                        <button
                            type="button"
                            onClick={() => {
                                onSearchChange?.('');
                                setSearchDropdownOpen?.(false);
                            }}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 cursor-pointer"
                            title="Clear search"
                        >
                            ✕
                        </button>
                    )}

                    {/* ── Search Results Dropdown (Spotlight / Quick-Find) ── */}
                    {searchDropdownOpen && searchQuery.trim().length > 0 && (
                        <div className="absolute top-full mt-2 right-0 w-80 sm:w-96 bg-white dark:bg-[#1e293b] rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 z-50 overflow-hidden">
                            <div className="px-4 py-2.5 bg-slate-50 dark:bg-slate-800/80 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center text-xs font-bold text-[#424750] dark:text-slate-300">
                                <span className="flex items-center gap-1.5">
                                    <SearchOutlined className="text-[#00AAF1]" />
                                    Search Results
                                </span>
                                {searchLoading ? (
                                    <span className="text-[#00AAF1] font-medium flex items-center gap-1 text-[11px]">
                                        Searching…
                                    </span>
                                ) : (
                                    <span className="text-slate-400 font-normal">
                                        {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
                                    </span>
                                )}
                            </div>

                            <div className="max-h-72 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
                                {searchLoading && searchResults.length === 0 ? (
                                    <div className="py-8 text-center text-xs text-slate-400">
                                        Searching tasks…
                                    </div>
                                ) : searchResults.length === 0 ? (
                                    <div className="py-8 text-center text-xs text-slate-400 px-4">
                                        No tasks found matching <span className="font-semibold text-slate-600 dark:text-slate-300">"{searchQuery}"</span>
                                    </div>
                                ) : (
                                    searchResults.map((task) => (
                                        <div
                                            key={task._id || task.id}
                                            onClick={() => {
                                                onTaskClick?.(task._id || task.id);
                                                setSearchDropdownOpen?.(false);
                                            }}
                                            className="p-3 hover:bg-[#f0f9ff] dark:hover:bg-slate-800/70 cursor-pointer transition-colors flex flex-col gap-1 group text-left"
                                        >
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="text-[11px] font-bold font-mono text-[#03488A] dark:text-[#28b5fd] group-hover:underline">
                                                    {task.taskId || 'Task'}
                                                </span>
                                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                                                    {task.status || 'Backlog'}
                                                </span>
                                            </div>
                                            <div className="text-xs font-semibold text-[#191c1d] dark:text-white truncate">
                                                {task.title}
                                            </div>
                                            <div className="flex items-center justify-between text-[11px] text-[#737782] dark:text-slate-400 mt-0.5">
                                                <span className="truncate max-w-[180px]">
                                                    {task.project?.title || task.project?.name || 'No Project'}
                                                </span>
                                                {task.priority && (
                                                    <span className="capitalize">{task.priority}</span>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                            <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800/60 border-t border-slate-100 dark:border-slate-700 text-[11px] text-center text-slate-400">
                                Click any task to view or edit in drawer
                            </div>
                        </div>
                    )}
                </div>

                {/* + New Task Button */}
                <button
                    type="button"
                    onClick={onNewTask}
                    className="text-white flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl font-semibold text-xs shadow-sm hover:opacity-90 active:scale-95 transition-all cursor-pointer flex-shrink-0"
                    style={{ background: 'linear-gradient(90deg, #03488A 0%, #00AAF1 100%)' }}
                >
                    <PlusOutlined style={{ fontSize: 12 }} />
                    <span>New Task</span>
                </button>
            </div>
        </div>
    );
}
